import { Component, Inject, Optional, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromModule from '../../ngrx/reducers';
import { AccountActions, ReservationActions, UserInfoActions } from '../../ngrx/actions';
import { Router, RouterLink } from '@angular/router';
import { cueUser, cueClose, cueArrowLeft, IconComponent } from '@cue/ui/icons';
import { AddinConfigService } from '@outlook-addin/shared';
import { translation } from '@cue/translations';
import {
  DropdownlistComponent,
  DropdownsItemTemplateDirective,
  ValueTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Localization, LOCALIZATIONS } from '@cue/translate';
import { OutlookReservationMode, STORAGE_KEYS } from '@outlook-addin/cue-http';
import { Observable } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

@Component({
  selector: 'addin-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  imports: [
    IconComponent,
    DropdownlistComponent,
    DropdownsItemTemplateDirective,
    TranslocoPipe,
    ValueTemplateDirective,
    ReactiveFormsModule,
    AsyncPipe,
  ],
})
export class MenuComponent {
  outlookReservationMode = OutlookReservationMode;
  currentOutlookReservationMode$: Observable<OutlookReservationMode>;
  isReservationLocationChangeActive$: Observable<boolean>;
  isReservationLocationChangeReturning$: Observable<boolean>;

  ICONS = {
    cueUser,
    cueClose,
    cueArrowLeft,
  };
  protected readonly translation = translation;

  protected instalationName: string;
  userInfo$ = this.store.select(fromModule.selectPackedUserInfo);

  httpClient = inject(HttpClient);
  version = rxResource({
    stream: () => this.httpClient.get<{ version: string }>('version.json').pipe(map((x) => x.version)),
  });
  currentLanguageControl = new FormControl<{ text: string; flag: string }>(null);
  public languageItems: Array<{ text: string; flag: string }> = [
    { text: 'CS', flag: 'cs.svg' },
    { text: 'CA', flag: 'ca.svg' },
    { text: 'UA', flag: 'ua.svg' },
    { text: 'EN', flag: 'en.svg' },
    { text: 'DE', flag: 'ge.svg' },
    { text: 'ES', flag: 'es.svg' },
    { text: 'FR', flag: 'fr.svg' },
    { text: 'AR', flag: 'ar.svg' },
    { text: 'PL', flag: 'pl.svg' },
  ];

  get loginUrl(): string {
    return `https://login.microsoftonline.com/${this.configService.value.configuration.tenantId}/oauth2/v2.0/authorize?client_id=${this.configService.value.configuration.clientId}
    &scope=Mail.Read offline_access openid profile User.Read&response_type=code&prompt=${this.configService.value.configuration.prompt}&redirect_uri=${`https://${window.location.host}/Authorize2`}`;
  }

  mobileHamburger = false;

  constructor(
    private store: Store<fromModule.State>,
    private router: Router,
    private configService: AddinConfigService,
    public translocoService: TranslocoService,
    @Optional() @Inject(LOCALIZATIONS) private localizations: Localization[][]
  ) {
    this.languageItems = this.configService.value.configuration.languages
      .filter((x) => x.activeInAssist)
      .map((x) => {
        return {
          text: x.code.toUpperCase(),
          flag: `${x.code}.svg`,
        };
      });

    this.currentOutlookReservationMode$ = this.store.select(fromModule.selectOutlookReservationMode);
    this.isReservationLocationChangeActive$ = this.store.select(fromModule.selectIsReservationLocationChangeActive);
    this.isReservationLocationChangeReturning$ = this.store.select(
      fromModule.selectIsReservationLocationChangeReturning
    );
    this.instalationName = this.configService.value.configuration.installationName;
    if (this.isUserLoggedIn()) {
      this.store.dispatch(UserInfoActions.loadUserInfo());
    }

    this.currentLanguageControl.setValue(this.currentLanguage, {
      emitEvent: false,
    });
    this.currentLanguageControl.valueChanges.subscribe((val) => {
      this.currentLanguage = val;
    });
  }

  get currentLanguage(): { text: string; flag: string } {
    const availableLangs: any[] = this.translocoService.getAvailableLangs();
    const langFromLocalStorage = localStorage.getItem(STORAGE_KEYS.addinLang);
    if (langFromLocalStorage != undefined) {
      const found = availableLangs.find((x) => x == langFromLocalStorage);
      return this.languageItems.find((x) => x.text == (found ?? this.translocoService.getDefaultLang()).toUpperCase())!;
    } else {
      const found = availableLangs.find((x) => x == navigator.language.slice(0, 2));
      return this.languageItems.find((x) => x.text == (found ?? this.translocoService.getDefaultLang()).toUpperCase())!;
    }
  }

  set currentLanguage(value: { text: string; flag: string }) {
    const availableLangs: any[] = this.translocoService.getAvailableLangs();
    const found = availableLangs.find((x) => x == value.text.toLowerCase());

    if (found) {
      const translation = this.localizations[0].find((x) => x.language == found)!;
      translation.valueGetter().then((x) => {
        localStorage.setItem(STORAGE_KEYS.addinLang, found);
        this.translocoService.setTranslation(x, translation.language, {
          merge: false,
        });
        this.translocoService.setActiveLang(found);
      });
    }
  }

  loginWithMsDialog() {
    Office.onReady().then((x) => {
      console.log(this.loginUrl);
      Office.context.ui.displayDialogAsync(
        this.loginUrl,
        { height: 20, width: 30, displayInIframe: false },
        (asyncResult) => {
          if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            window.dispatchEvent(new CustomEvent('cue-popup-blocked'));
            return;
          }

          const dialog = asyncResult.value;
          dialog.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
            // console.log(args);
            this.store.dispatch(AccountActions.loadTokenRefreshToken({ route: false }));
          });
        }
      );
    });
    //Close hamburger menu
    this.mobileHamburger = false;
  }

  isUserLoggedIn(): boolean {
    return localStorage.getItem(STORAGE_KEYS.code) != null;
  }

  // logout(){
  //
  //   this.authHttpService.signOut().subscribe(()=> {
  //     this.store.dispatch(UserInfoActions.clearUserInfo());
  //     this.mobileHamburger = false;
  //     this.cdr.markForCheck();
  //   });
  // }

  navigateToReservations() {
    this.router.navigate(['reservations']);
    //Close hamburger menu
    this.mobileHamburger = false;
  }

  navigateToReservationList() {
    this.store.dispatch(ReservationActions.startReservationLocationChange());
    this.store.dispatch(
      ReservationActions.outlookReservationModeChange({
        reservationMode: OutlookReservationMode.NotReservationDetail,
      })
    );
    this.router.navigate(['reservations']);
  }

  navigateBackToReservationOverview() {
    this.store.dispatch(ReservationActions.returnFromReservationLocationChange());
    this.store.dispatch(ReservationActions.refreshReservationDetail());
  }
}
